import { UserRoundCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { coverStatusLabel, coverStatusTone, dateRange, dutyLabel } from '../cover'
import type { Cover, Period } from '../types'
import { formatTime, subjectDotStyle, weekdayName } from '../week'

/** The covers a teacher takes, and the ones standing in for them, with the lessons involved. */
export function MyCoversCard({
  covers,
  coveredBy,
  periods,
}: {
  covers: Cover[]
  coveredBy: Cover[]
  periods: Period[]
}) {
  const { t } = useTranslation('timetable')
  if (covers.length === 0 && coveredBy.length === 0) return null
  const periodOf = new Map(periods.map((p) => [p.id, p]))

  return (
    <Card>
      <CardHeader
        icon={<UserRoundCheck />}
        title={t('Cover')}
        description={t('Classes and lessons you take over for colleagues, and who stands in for you.')}
      />
      <ul className="divide-y divide-line">
        {covers.map((c) => (
          <li key={c.id} className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-start">
            <div className="flex min-w-0 flex-1 items-start gap-2.5">
              <span
                className="mt-1.5 size-2.5 shrink-0 rounded-full bg-brand-500"
                style={subjectDotStyle(c.subject?.color ?? null)}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">
                  {dutyLabel(c.kind, c.subject)} · {c.section.name}
                </p>
                <p className="text-xs text-muted">
                  {c.absentTeacher ? `${t('For {{name}}', { name: c.absentTeacher.name })} · ` : ''}
                  {dateRange(c.startDate, c.endDate)}
                </p>
                {c.lessons.length > 0 ? (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {c.lessons.map((l) => {
                      const p = periodOf.get(l.periodId)
                      return (
                        <li
                          key={l.id}
                          className="rounded-full bg-surface-sunken px-2 py-0.5 text-2xs font-medium text-ink-soft"
                        >
                          {weekdayName(l.dayOfWeek, 'short')}{' '}
                          {p ? `${p.name} · ${formatTime(p.startTime)}` : ''}
                          {l.room ? ` · ${l.room.name}` : ''}
                        </li>
                      )
                    })}
                  </ul>
                ) : c.kind === 'section' ? (
                  <p className="mt-1 text-xs text-ink-soft">
                    {t('You take the register and can see the roster and enter every subject’s marks.')}
                  </p>
                ) : null}
                {c.note ? <p className="mt-1 text-xs text-ink-soft italic">{c.note}</p> : null}
              </div>
            </div>
            <Badge tone={coverStatusTone[c.status]} size="sm" className="self-start">
              {t(coverStatusLabel[c.status])}
            </Badge>
          </li>
        ))}
        {coveredBy.map((c) => (
          <li key={`by-${c.id}`} className="flex flex-col gap-2 px-5 py-3.5 sm:flex-row sm:items-start">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ink">
                {t('{{name}} covers your {{what}} in {{section}}', {
                  name: c.substitute.name,
                  what: c.kind === 'section' ? t('class teacher duties') : (c.subject?.name ?? ''),
                  section: c.section.name,
                })}
              </p>
              <p className="text-xs text-muted">{dateRange(c.startDate, c.endDate)}</p>
            </div>
            <Badge tone="neutral" size="sm" className="self-start">
              {t(coverStatusLabel[c.status])}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  )
}
